import { useState } from "react";
import { Plus, Search, Users } from "lucide-react";

import { orgUsersApi } from "@/api/auth";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Pagination } from "@/components/Pagination";
import { Toast } from "@/components/Toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { usePermissions } from "@/hooks/usePermissions";
import { useResourceList } from "@/hooks/useResourceList";

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Administrator",
  coordinator: "Care coordinator",
  caregiver: "Caregiver",
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const COLUMNS = [
  {
    key: "full_name",
    header: "Name",
    render: (row) => (
      <span className="font-medium">
        {row.first_name || row.last_name
          ? `${row.first_name || ""} ${row.last_name || ""}`.trim()
          : "—"}
      </span>
    ),
  },
  { key: "email", header: "Email" },
  {
    key: "role",
    header: "Role",
    render: (row) => ROLE_LABELS[row.role] ?? row.role,
  },
  {
    key: "is_active",
    header: "Status",
    render: (row) => (
      <Badge variant={row.is_active ? "default" : "secondary"}>
        {row.is_active ? "Active" : "Inactive"}
      </Badge>
    ),
  },
];

const EMPTY_FORM = { email: "", first_name: "", last_name: "", role: "coordinator" };

export function OrgUsersPage() {
  const { can } = usePermissions();
  const canManage = can("organization.users.manage");

  const { rows, count, isLoading, error, page, setPage, search, setSearch, reload } =
    useResourceList(orgUsersApi);

  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const closeInvite = () => {
    setShowInvite(false);
    setTempPassword(null);
    setFormError(null);
    setForm(EMPTY_FORM);
  };

  const discardInvite = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const inviteDirty =
    form.email.trim() !== "" ||
    form.first_name.trim() !== "" ||
    form.last_name.trim() !== "" ||
    form.role !== EMPTY_FORM.role;

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const created = await orgUsersApi.invite(form);
      setTempPassword(created.temp_password);
      setForm(EMPTY_FORM);
      reload();
    } catch (err) {
      setFormError(err.message || "Failed to invite user.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await orgUsersApi.update(user.id, { is_active: !user.is_active });
      setToastMessage(
        user.is_active
          ? `${user.email} has been deactivated.`
          : `${user.email} has been reactivated.`,
      );
      reload();
    } catch {
      setToastMessage("Failed to update user status.");
    }
  };

  const actionColumn = canManage
    ? [
        {
          key: "actions",
          header: "",
          render: (row) => (
            <Button
              type="button"
              size="sm"
              variant={row.is_active ? "destructive" : "outline"}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(row);
              }}
            >
              {row.is_active ? "Deactivate" : "Reactivate"}
            </Button>
          ),
        },
      ]
    : [];

  if (isLoading && rows.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />

      <PageHeader
        title="Team Members"
        description="Manage users in your organization."
        actions={
          canManage ? (
            <Button
              type="button"
              variant={showInvite ? "outline" : "default"}
              onClick={() => {
                if (showInvite) {
                  closeInvite();
                } else {
                  setShowInvite(true);
                  setTempPassword(null);
                  setFormError(null);
                }
              }}
            >
              {showInvite ? (
                "Cancel"
              ) : (
                <>
                  <Plus />
                  Invite user
                </>
              )}
            </Button>
          ) : null
        }
      />

      <PageBody>
        {showInvite ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Invite a new team member</CardTitle>
              <CardDescription>
                They receive a temporary password. Share it once — it will not be shown again.
              </CardDescription>
            </CardHeader>
            {tempPassword ? (
              <CardContent className="space-y-4">
                <Alert type="info">
                  User invited. Share this temporary password with them — it will not be shown
                  again:
                  <br />
                  <code className="mt-2 block text-base tracking-wide">{tempPassword}</code>
                </Alert>
              </CardContent>
            ) : (
              <form onSubmit={handleInvite}>
                <CardContent className="space-y-4">
                  <Alert type="error">{formError}</Alert>
                  <Field label="Email address" name="email">
                    {(props) => (
                      <Input
                        {...props}
                        type="email"
                        value={form.email}
                        required
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      />
                    )}
                  </Field>
                  <Field label="First name" name="first_name">
                    {(props) => (
                      <Input
                        {...props}
                        value={form.first_name}
                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                      />
                    )}
                  </Field>
                  <Field label="Last name" name="last_name">
                    {(props) => (
                      <Input
                        {...props}
                        value={form.last_name}
                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                      />
                    )}
                  </Field>
                  <Field label="Role" name="role">
                    {(props) => (
                      <FormSelect
                        id={props.id}
                        name={props.name}
                        value={form.role}
                        onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
                        options={ROLE_OPTIONS}
                        placeholder="Select role…"
                      />
                    )}
                  </Field>
                </CardContent>
                <CardFooter className="gap-2 border-t border-border px-4 pt-4">
                  <Button type="submit" disabled={saving || !form.email.trim()}>
                    {saving ? "Inviting…" : "Send invite"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={saving || !inviteDirty}
                    onClick={discardInvite}
                  >
                    Discard
                  </Button>
                </CardFooter>
              </form>
            )}
          </Card>
        ) : null}

        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or email…"
        />

        <Alert type="error">{error}</Alert>

        {rows.length === 0 ? (
          <EmptyState
            icon={search ? Search : Users}
            tone={search ? "slate" : "indigo"}
            title={search ? "No matches for that search." : "No team members found"}
            message={
              search
                ? "Try a different name or email."
                : "Invite your first team member to get started."
            }
            action={
              !search && canManage ? (
                <Button
                  type="button"
                  onClick={() => {
                    setShowInvite(true);
                    setTempPassword(null);
                    setFormError(null);
                  }}
                >
                  <Plus />
                  Invite user
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable
            columns={[...COLUMNS, ...actionColumn]}
            rows={rows}
            getRowKey={(row) => row.id}
          />
        )}

        <Pagination page={page} count={count} onChange={setPage} />
      </PageBody>
    </PageShell>
  );
}
